import { useCallback, useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { getConfig } from "@/lib/config";
import { doorbellCallsApi, iceServersFrom } from "@/lib/api/doorbell-calls";
import type { CallMode } from "@/lib/doorbell-call/call-status";
import { freshCallSocketUrl } from "@/lib/doorbell-call/socket-url";
import {
  INITIAL_SNAPSHOT,
  startCallSession,
  type CallSession,
  type CallSnapshot,
  type CallSocket,
  type PeerLike,
} from "@/lib/doorbell-call/session";

const ACTIVE_CALL_KEY = "nodus.portal.doorbellCall.active";

/** The call this tab answered, so a reload rejoins it instead of showing the outcome. */
export function activeCallId(): number | null {
  try {
    const raw = window.sessionStorage.getItem(ACTIVE_CALL_KEY);
    return raw ? Number(raw) : null;
  } catch {
    return null;
  }
}

function rememberActiveCall(callId: number | null): void {
  try {
    if (callId === null) window.sessionStorage.removeItem(ACTIVE_CALL_KEY);
    else window.sessionStorage.setItem(ACTIVE_CALL_KEY, String(callId));
  } catch {
    // Storage blocked: a reload then shows the outcome instead of rejoining.
  }
}

export interface DoorbellCall {
  snapshot: CallSnapshot;
  accept: (video: boolean) => void;
  setMuted: (muted: boolean) => void;
  setCamera: (on: boolean) => void;
  hangup: () => void;
}

/**
 * The member's side of call `callId`, wired to the browser (WebSocket,
 * getUserMedia, RTCPeerConnection) — all logic lives in
 * `lib/doorbell-call/session.ts`. Starts once `mode` is set; leaving the page
 * while on the call hangs up.
 */
export function useDoorbellCall({ callId, isTest, mode }: { callId: number; isTest: boolean; mode: CallMode }): DoorbellCall {
  const [snapshot, setSnapshot] = useState<CallSnapshot>(INITIAL_SNAPSHOT);
  const sessionRef = useRef<CallSession | null>(null);

  useEffect(() => {
    if (mode === null || !Number.isInteger(callId)) return;
    const session = startCallSession({
      isTest,
      socketUrl: () =>
        freshCallSocketUrl({
          apiBaseUrl: getConfig().API_BASE_URL,
          callId,
          refresh: (signal) => doorbellCallsApi.get(callId, { signal }),
          readToken: () => localStorage.getItem("access_token"),
        }),
      createSocket: (url) => new WebSocket(url) as unknown as CallSocket,
      getUserMedia: (constraints) => navigator.mediaDevices.getUserMedia(constraints),
      createPeerConnection: (config) => new RTCPeerConnection(config) as unknown as PeerLike,
      fetchIceServers: () => doorbellCallsApi.ice(callId).then((r) => iceServersFrom(r.data)),
      makeStream: (tracks) => new MediaStream(tracks),
      onChange: setSnapshot,
      onError: (err) => Sentry.captureException(err, { tags: { kind: "doorbell-call" } }),
    });
    sessionRef.current = session;
    if (mode === "rejoin") void session.rejoin();
    else session.join();
    return () => {
      session.hangup(); // a no-op unless this page is on the call
      session.dispose();
      if (sessionRef.current === session) sessionRef.current = null;
    };
  }, [callId, isTest, mode]);

  useEffect(() => {
    if (snapshot.phase === "connected") rememberActiveCall(callId);
    else if (snapshot.phase === "ended" || snapshot.phase === "taken" || snapshot.phase === "replaced" || snapshot.phase === "failed") rememberActiveCall(null);
  }, [snapshot.phase, callId]);

  const accept = useCallback((video: boolean) => {
    void sessionRef.current?.accept(video);
  }, []);
  const setMuted = useCallback((muted: boolean) => sessionRef.current?.setMuted(muted), []);
  const setCamera = useCallback((on: boolean) => {
    void sessionRef.current?.setCamera(on);
  }, []);
  const hangup = useCallback(() => sessionRef.current?.hangup(), []);

  return { snapshot, accept, setMuted, setCamera, hangup };
}
